import * as React from 'react';
import moment from 'moment-timezone';
import { DateRangePickerProps } from './DateRangePicker';
import { useDateTimeFormat } from './useDateTimeFormat';

export type DateRange = { from: string; to: string };

export type UseDateRangePickerArgs = Pick<DateRangePickerProps, 'errors' | 'type'> & {
    initialFrom?: Date | string;
    initialTo?: Date | string;
    onChange?: (range: Partial<DateRange>) => void;
    shouldKeepDatesInOrder?: boolean;
    shouldReportErrorOnToAfterFrom?: boolean;
};

export type UseDateRangePickerResponse = [
    Pick<DateRangePickerProps, 'errors' | 'from' | 'onFromChange' | 'onToChange' | 'to'>,
];

export const useDateRangePicker = (args: UseDateRangePickerArgs): UseDateRangePickerResponse => {
    const format = useDateTimeFormat(args.type);

    const [from, setFrom] = React.useState(args.initialFrom ? moment(args.initialFrom).format(format) : '');
    const [to, setTo] = React.useState(args.initialTo ? moment(args.initialTo).format(format) : '');

    const handleFromChange = React.useCallback(
        (newValue: string) => {
            let newFrom = moment(newValue);
            const newTo = to ? moment(to) : undefined;
            if (newTo && newFrom.isAfter(to) && args.shouldKeepDatesInOrder) {
                newFrom = newTo;
            }
            setFrom(newFrom.format(format));
            args.onChange?.({ from: newFrom.format(format), to: newTo?.format(format) });
        },
        [args, format, to],
    );

    const handleToChange = React.useCallback(
        (newValue: string) => {
            const newFrom = from ? moment(from) : undefined;
            let newTo = moment(newValue);
            if (newFrom && newTo.isBefore(newFrom) && args.shouldKeepDatesInOrder) {
                newTo = newFrom;
            }
            setTo(newTo.format(format));
            args.onChange?.({ from: newFrom?.format(format), to: newTo.format(format) });
        },
        [args, format, from],
    );

    const toErrorText = React.useMemo(() => {
        if (args.errors?.to) {
            return args.errors.to;
        } else if (args.shouldReportErrorOnToAfterFrom) {
            if (moment(to).isBefore(moment(from))) {
                return 'Invalid date range selected';
            }
        }
        return '';
    }, [args.errors, args.shouldReportErrorOnToAfterFrom, from, to]);

    return [
        {
            errors: { from: args.errors?.from, to: toErrorText },
            from,
            onFromChange: handleFromChange,
            onToChange: handleToChange,
            to,
        },
    ];
};
